import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { appointmentEndpoints } from '@/lib/api/endpoints';
import { jobKeys, schedulerKeys } from '@/lib/queryKeys';
import type { ApiError } from '@/lib/api/errors';
import type {
  CancelSeriesDto,
  CancelSeriesResponse,
  ReassignSeriesDto,
  ReassignSeriesResponse,
  RescheduleSeriesDto,
  RescheduleSeriesResponse,
  SeriesConflictDay,
} from '@/types/jobAppointment';

/**
 * Multi-day ALL_DAY series actions (Scheduling v2 §5). Each mutation acts on
 * the REMAINING days only (non-terminal, today or later in the tenant-local
 * day) — past days stay untouched — and re-validates every affected day
 * server-side before one atomic commit.
 */

function invalidateSeriesQueries(queryClient: ReturnType<typeof useQueryClient>, jobId: string) {
  void queryClient.invalidateQueries({ queryKey: jobKeys.appointments(jobId) });
  void queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
  void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
  // Every series day is a calendar booking — refresh the scheduler surfaces too.
  void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
}

/**
 * The 400/409 payloads carry per-day details: 409 conflicts as
 * `{ days: [{ date, conflicts: [{ message }] }] }`, 400 (weekend / no-shift
 * days) as a plain message listing the dates. Normalized here so dialogs can
 * render them inline instead of a lossy toast.
 */
export function seriesErrorDays(error: ApiError | null): SeriesConflictDay[] {
  const raw = error?.data as { days?: unknown } | undefined;
  if (!raw || !Array.isArray(raw.days)) return [];
  return raw.days.filter(
    (day): day is SeriesConflictDay =>
      typeof day === 'object' &&
      day !== null &&
      typeof (day as { date?: unknown }).date === 'string',
  );
}

export function useRescheduleSeries(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<RescheduleSeriesResponse, { seriesId: string } & RescheduleSeriesDto>({
    mutationFn: ({ seriesId, ...dto }) =>
      apiPost<RescheduleSeriesResponse>(
        operationsClient,
        appointmentEndpoints.seriesReschedule(seriesId),
        dto,
      ),
    // The dialog renders the per-day error details inline (not just a toast).
    showErrorToast: false,
    onSuccess: (data) => {
      invalidateSeriesQueries(queryClient, jobId);
      toast.success(
        `Series rescheduled — ${data.moved} remaining ${data.moved === 1 ? 'day' : 'days'} moved`,
      );
    },
  });
}

export function useReassignSeries(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<ReassignSeriesResponse, { seriesId: string } & ReassignSeriesDto>({
    mutationFn: ({ seriesId, ...dto }) =>
      apiPost<ReassignSeriesResponse>(
        operationsClient,
        appointmentEndpoints.seriesReassign(seriesId),
        dto,
      ),
    // The dialog renders the per-day error details inline (not just a toast).
    showErrorToast: false,
    onSuccess: (data) => {
      invalidateSeriesQueries(queryClient, jobId);
      toast.success(
        `Series reassigned — ${data.reassigned} remaining ${data.reassigned === 1 ? 'day' : 'days'} moved to the new technician`,
      );
    },
  });
}

export function useCancelSeries(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<CancelSeriesResponse, { seriesId: string } & CancelSeriesDto>({
    mutationFn: ({ seriesId, ...dto }) =>
      apiPost<CancelSeriesResponse>(
        operationsClient,
        appointmentEndpoints.seriesCancel(seriesId),
        dto,
      ),
    onSuccess: (data) => {
      invalidateSeriesQueries(queryClient, jobId);
      toast.success(
        `Series cancelled — ${data.cancelled} remaining ${data.cancelled === 1 ? 'day' : 'days'} cancelled`,
      );
    },
  });
}
